import './YandexMap.scss';
import { LitElement, html } from 'lit';
import loadScript from '../../../modules/load-script';

export class YandexMap extends LitElement {
    constructor() {
        super();

        this._closeCard = this._closeCard.bind(this);

        this.instance = null;
        this.zoom = 11;
        this._isFetching = false;
        this.currentCoords = [0, 0];
        this.data = this.getAttribute('map-data') ? JSON.parse(this.getAttribute('map-data')) : null;
        this.marks = [];
        this.activeTabs = [];
        this.clusterer;
        this.isCardVisible = false;

        this._observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    obs.unobserve(entry.target);
                    this._init();
                }
            });
        });
    }

    static get properties() {
        return {
            centerLat: {
                type: Number,
                attribute: 'center-lat',
                reflect: true,
            },
            centerLng: {
                type: Number,
                attribute: 'center-lng',
                reflect: true,
            },
            apiKey: {
                type: String,
                attribute: 'api-key',
            },
            zoom: {
                type: Number,
                reflect: true,
            },
            marker: {
                type: String,
                reflect: true,
            },
            data: {
                type: Object,
            },
            _isFetching: {
                type: Boolean,
                attribute: false,
            },
            _error: {
                attribute: false,
            },
            isCardVisible: {
                type: Boolean,
            },
        };
    }

    createRenderRoot() {
        return this;
    }

    _init() {
        this._error = null;
        this._isFetching = true;
        loadScript(`https://api-maps.yandex.ru/2.1/?apikey=${this.apiKey}&lang=ru_RU`)
            .then(() => {
                const { ymaps } = window;
                ymaps.ready(() => {
                    const _this = this;
                    const margin = window.matchMedia('(max-width: 1024px)').matches ? 50 : [50, 50, 50, 400];
                    this.instance = new ymaps.Map(
                        this.renderRoot.querySelector('.map'),
                        {
                            center: [this.centerLat, this.centerLng],
                            zoom: this.zoom,
                            margin: margin,
                        },
                        { smoothZooming: true, smoothScroll: true, suppressMapOpenBlock: true },
                    );
                    this.instance.container.fitToViewport();

                    const zoomControl = new ymaps.control.ZoomControl({
                        options: {
                            position: {
                                left: 'auto',
                                right: 10,
                                top: 60,
                            },
                        },
                    });
                    this.instance.controls.add(zoomControl);

                    const elements = [];

                    if (this.data) {
                        if (this.data.placemarks?.[0]?.coords) {
                            this.currentCoords = this.data.placemarks[0].coords;
                        }

                        this.data.placemarks.forEach((placemark) => {
                            const animatedLayout = ymaps.templateLayoutFactory.createClass(
                                `<div class="map-placemark js-map-placemark" data-coords="${placemark.coords}">
                                    <div class="map-placemark__button js-map-marker">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                                            <path d="M40 0H20H0V20V40H20H40V20V0Z" fill="#2F2C2B"/>
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M19.9882 27.8428C19.8937 26.6136 19.941 25.5735 19.941 24.3207C19.941 24.0134 20.673 23.7061 21.2634 23.3751C21.9482 22.9733 25.4196 20.8931 25.6558 20.8931C25.8683 20.8931 29.9065 23.2806 30.7566 23.7533C31.04 23.9188 31.3942 24.0134 31.3942 24.3207C31.3942 25.4553 31.4414 26.7318 31.347 27.8428C30.8983 27.5355 25.8447 24.628 25.6558 24.628C25.3724 24.628 20.6022 27.4882 19.9882 27.8428ZM8.55858 21.8623V18.1274C9.40872 18.5056 13.8483 21.3422 14.2498 21.3422C14.6748 21.3659 25.2543 14.9125 25.703 14.8416C25.8919 14.818 30.8747 17.7255 31.4414 18.0565L31.465 21.7913C30.5677 21.4131 26.0808 18.6001 25.7502 18.5765C25.5377 18.5765 20.6494 21.4368 20.0118 21.815C19.4214 22.1696 14.4859 25.0535 14.3206 25.0771C13.99 25.1244 9.36149 22.2405 8.55858 21.8623ZM8.53497 15.669L8.55858 12.1232C9.17257 12.4778 13.99 15.3144 14.1789 15.338C14.7221 15.4089 18.9255 12.5487 19.9882 12.1705V15.8581C19.2325 16.3781 14.4387 19.1202 14.1789 19.0966L9.14896 16.2363C8.84196 16.0472 8.53497 15.9999 8.53497 15.669ZM7 28.9302C9.03088 29.072 15.7847 28.9538 18.2879 28.9538L33 28.9302V11.0594C31.7956 10.9413 10.8492 11.0358 7 11.0358V28.9302Z" fill="white"/>
                                        </svg>
                                    </div>
                                    <div class="map-card">
                                        <button
                                            class="card-close-btn"
                                            aria-label="Закрыть"
                                            @click="${this._closeCard}"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 1L11 11M1 11L11 1" stroke="#3B3B3B"/>
                                            </svg>
                                        </button>
                                        <div class="map-card__inner">
                                            <div class="map-card__title">
                                                ${placemark.address ? placemark.address : ''}
                                            </div>
                                            <div class="map-card__time">
                                                ${placemark.working_mode ? placemark.working_mode : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>`,
                                {
                                    build() {
                                        animatedLayout.superclass.build.call(this);
                                        const element =
                                            this.getParentElement().getElementsByClassName('map-placemark')[0];
                                        elements.push(element);
                                        // Если метка выбрана, то увеличим её размер.
                                        const size = 40;
                                        // Зададим фигуру активной области.
                                        this.getData().options.set('shape', {
                                            type: 'Circle',
                                            coordinates: [size / 2, size / 2],
                                            radius: size / 2,
                                        });

                                        // Если метка выбрана, то зададим класс и запустим анимацию.
                                        if (this.isActive) {
                                            element.classList.add('is-active');
                                        } else if (this.inited) {
                                            element.classList.remove('is-active');
                                        }

                                        const closeAll = () => {
                                            _this.isCardVisible = false;
                                            if (_this.activeMark && _this.activeMark !== this) {
                                                _this.activeMark.isActive = false;
                                            }
                                            _this.renderRoot
                                                .querySelector('.map-card.is-active')
                                                ?.classList.remove('is-active');
                                            elements.forEach((el) => {
                                                el.classList.remove('is-active');
                                            });
                                        };

                                        _this.addEventListener('overlay-click', closeAll);

                                        if (!this.inited) {
                                            this.inited = true;
                                            this.isActive = false;

                                            let offsetY = 0.05;
                                            let offsetX = 0.08;

                                            if (window.matchMedia('(max-width: 767px)').matches) {
                                                offsetY = 0.01;
                                                offsetX = 0.07;
                                            }

                                            // При клике по метке будем перестраивать макет.
                                            this.getData().geoObject.events.add(
                                                'click',
                                                function (event) {
                                                    closeAll();

                                                    const mark = event.get('target');
                                                    const index = _this.marks.indexOf(mark);
                                                    const coords = mark.geometry?._coordinates;

                                                    if (coords && _this.instance) {
                                                        _this.instance
                                                            .setCenter(
                                                                [
                                                                    Number(coords[0]) + offsetY,
                                                                    Number(coords[1]) + offsetX,
                                                                ],
                                                                11,
                                                                {
                                                                    checkZoomRange: true,
                                                                    duration: 3,
                                                                },
                                                            )
                                                            .then(() => {
                                                                this.isActive = !this.isActive;
                                                                _this.isCardVisible = this.isActive;
                                                                if (this.isActive) {
                                                                    element.classList.add('is-active');
                                                                    if (index > -1) {
                                                                        element
                                                                            .querySelector('.map-card')
                                                                            .classList.add('is-active');
                                                                    }
                                                                }
                                                                _this.activeMark = this;
                                                            });
                                                    }
                                                },
                                                this,
                                            );
                                            this.getData().geoObject.events.add(
                                                'mouseenter',
                                                function () {
                                                    element.classList.add('is-hovered');
                                                },
                                                this,
                                            );
                                            this.getData().geoObject.events.add(
                                                'mouseleave',
                                                function () {
                                                    element.classList.remove('is-hovered');
                                                },
                                                this,
                                            );
                                        }
                                    },
                                },
                            );

                            const markOptions = {
                                iconLayout: animatedLayout,
                                hasHint: false,
                                hasBalloon: false,
                            };
                            const mark = new ymaps.Placemark(placemark.coords, {}, markOptions);
                            this.marks.push(mark);
                            this.instance.geoObjects.add(mark);
                        });

                        this.dispatchEvent(new Event('init'));

                        this.clusterer = new ymaps.Clusterer({
                            clusterIconLayout: ymaps.templateLayoutFactory.createClass(`
                                <div class="cluster-icon">
                                    <span class="cluster-icon__count">{{ properties.geoObjects.length }}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="30" viewBox="0 0 42 30" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M20.9809 28.0714C20.8283 26.0227 20.9046 24.2892 20.9046 22.2011C20.9046 21.689 22.0872 21.1768 23.0409 20.6252C24.1471 19.9555 29.7548 16.4885 30.1362 16.4885C30.4796 16.4885 37.0027 20.4676 38.376 21.2556C38.8338 21.5314 39.406 21.689 39.406 22.2011C39.406 24.0922 39.4823 26.2197 39.3297 28.0714C38.6049 27.5592 30.4414 22.7133 30.1362 22.7133C29.6785 22.7133 21.9728 27.4804 20.9809 28.0714ZM2.51771 18.1038V11.879C3.89101 12.5093 11.0627 17.237 11.7112 17.237C12.3978 17.2764 29.4877 6.52088 30.2125 6.40269C30.5177 6.36329 38.5668 11.2092 39.4823 11.7608L39.5204 17.9856C38.0708 17.3552 30.8229 12.6669 30.2888 12.6275C29.9455 12.6275 22.049 17.3946 21.0191 18.025C20.0654 18.6159 12.0926 23.4224 11.8256 23.4618C11.2916 23.5406 3.81471 18.7341 2.51771 18.1038ZM2.47956 7.78161L2.51771 1.87197C3.50954 2.46293 11.2916 7.19064 11.5967 7.23004C12.4741 7.34823 19.2643 2.58113 20.9809 1.95076V8.09679C19.7602 8.96353 12.0163 13.5337 11.5967 13.4943L3.47139 8.72715C2.97548 8.41197 2.47956 8.33317 2.47956 7.78161ZM0 29.8837C3.28065 30.12 14.1907 29.9231 18.2343 29.9231L42 29.8837V0.0990768C40.0545 -0.0979112 6.21798 0.0596792 0 0.0596792V29.8837Z" fill="white"/>
                                    </svg>
                                </div>`),

                            // Чтобы метка была кликабельной, переопределим ее активную область.
                            clusterIconShape: {
                                type: 'Rectangle',
                                coordinates: [
                                    [0, 0],
                                    [60, 60],
                                ],
                            },
                            hasBalloon: false,
                            hasHint: false,
                            groupByCoordinates: true,
                            gridSize: 128,
                        });

                        this.clusterer.add(this.marks);
                        this.instance.geoObjects.add(this.clusterer);
                    }

                    this.instance.behaviors.disable('scrollZoom');

                    this.instance.events.add('click', this._closeCard);

                    window.addEventListener('resize', this._onResize);

                    const mapLinks = document.querySelectorAll('.js-map-link');
                    mapLinks.forEach((link) => link.addEventListener('click', () => this._setCoords(link)));

                    // смотрим, еслть ли офис в городе и открываем попап этого офиса
                    this._findOffice();
                });
            })
            .catch((err) => {
                this._error = err;
                throw err;
            })
            .finally(() => {
                this._isFetching = false;
            });
    }

    connectedCallback() {
        super.connectedCallback();

        if (!this.apiKey) {
            throw new Error('API key not provided.');
        }

        this._observer.observe(this);
    }

    attributeChangedCallback(name, oldVal, newVal) {
        super.attributeChangedCallback(name, oldVal, newVal);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this._observer.disconnect();
        window.removeEventListener('resize', this._onResize);
        this._error = null;

        if (this.instance) {
            this.instance.destroy();
            this.instance = null;
        }
    }

    _onResize() {
        this.instance?.container.fitToViewport();
    }

    _renderMap() {
        if (this._isFetching) {
            return html`<div class="map-loader">Загружаем карту...</div>`;
        }

        if (this._error) {
            return html`<div>${this._error.message}</div>`;
        }

        return '';
    }

    _closeCard() {
        this.dispatchEvent(new Event('overlay-click', { composed: true }));
    }

    _hoverPoint(coords) {
        let offsetY = 0.05;
        let offsetX = 0.08;

        if (window.matchMedia('(max-width: 767px)').matches) {
            offsetY = 0.01;
            offsetX = 0.07;
        }
        setTimeout(() => {
            const placemarksOnMap = document.querySelectorAll('.js-map-placemark');
            placemarksOnMap.forEach((mark) => {
                const markCoords = mark.dataset.coords ? mark.dataset.coords.split(',') : [];
                if (markCoords.length && markCoords[0] == coords[0] && markCoords[1] == coords[1]) {
                    mark.classList.add('is-hovered', 'is-active');

                    // показываем попап
                    const pointPopup = mark.querySelector('.map-card');
                    if (pointPopup) pointPopup.classList.add('is-active');

                    // сдвигаем карту чуть ниже и левее, чтобы было видно поп ап
                    if (this.instance) {
                        this.instance.setCenter([Number(coords[0]) + offsetY, Number(coords[1]) + offsetX], 11);
                    }
                } else {
                    mark.classList.remove('is-hovered');
                }
            });
        }, 200);
    }

    _findOffice() {
        // Если у офиса есть адрес (режим работы), то открываем попап офиса
        const offices = document.querySelectorAll('.js-map-search-office:not(.is-hide)');
        if (offices.length == 1) {
            const address = offices[0].querySelector('.js-work-time');

            if (address) {
                const coordsContainer = offices[0].querySelector('.js-map-link');
                const officeCoords = coordsContainer.dataset.coords ? JSON.parse(coordsContainer.dataset.coords) : null;

                if (officeCoords) {
                    this._hoverPoint(officeCoords);
                }
            }
        }
    }

    _setCoords(link) {
        const coords = link.dataset.coords ? JSON.parse(link.dataset.coords) : null;
        if (this.instance && coords) {
            link.classList.contains('js-map-link-city')
                ? this.instance.setCenter(coords, 11)
                : this.instance.setCenter(coords, 15);
            // если это кнопка "Показать на карте"
            if (!link.classList.contains('js-map-link-city')) {
                this._hoverPoint(coords);
            } else {
                // если это выбор города
                this._findOffice();
            }
        }
    }

    render() {
        return html` <div class="map-container">
            <div class="map">${this._renderMap()}</div>
        </div>`;
    }
}
